import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { UserPreferences } from "../../common/Constants";
import SaveLanguageToStorageInteractor from "../../interactor/credential/SaveLanguageToStorageInteractor";
import PasswordResetCredentials, {
  IPasswordResetCredentials,
  TPasswordResetCredentials
} from "../../model/authentication/PasswordResetCredentials";
import { IValidationResult } from "../../model/validation/ValidationResult";
import RequestPasswordResetInteractor from "../../interactor/credential/RequestPasswordResetInteractor";
import ConfirmPasswordResetInteractor from "../../interactor/credential/ConfirmPasswordResetInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";

export interface TPasswordResetPresenter extends TLoadingAwarePresenter {
  languageCode: string;
  credentials: IPasswordResetCredentials;
  validationErrors?: any;
  disableSubmit?: boolean;
  confirmationMessage?: string;
  confirmationSuccess?: boolean;
}

export interface IPasswordResetPresenter
  extends TPasswordResetPresenter,
    TPresentable {
  changeLanguage(languageCode: string): void;
  setLanguageCode(languageCode: string): void;
  setCredentialsValue(name: string, value: string): void;
  submit(): Promise<void>;
  confirmPasswordReset(userId: string, token: string): Promise<void>;
}

const defaultState: TPasswordResetPresenter = {
  languageCode: UserPreferences.DEFAULT_LANGUAGE,
  credentials: PasswordResetCredentials({} as TPasswordResetCredentials),
  validationErrors: {} as any,
  disableSubmit: false,
  confirmationMessage: " "
};

const PasswordResetPresenter = withStore<
  IPasswordResetPresenter,
  TPasswordResetPresenter
>(({ application, store, loader, translate }): IPasswordResetPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TPasswordResetPresenter>();

  const setLanguageCode = (languageCode: string) => {
    _store.update({
      languageCode
    });
  };

  const changeLanguage = async (languageCode: string) => {
    setLanguageCode(languageCode);

    _application.container.registerValue("language", languageCode);

    await _application.container
      .resolve<SaveLanguageToStorageInteractor>("saveLanguageToStorage")
      .execute(languageCode);

    let { credentials, validationErrors } =
      _store.getState<TPasswordResetPresenter>();

    if (Object.keys(validationErrors).length) {
      validate(credentials);
    }
  };

  const setCredentialsValue = (key: string, value: any) => {
    let credentials = _store.getState<TPasswordResetPresenter>().credentials;
    const validationErrors =
      _store.getState<TPasswordResetPresenter>().validationErrors;

    credentials[key] = value;
    _store.update({
      credentials
    });
    if (Object.keys(validationErrors).length) {
      validate(credentials);
    }
  };

  const validate = async (credentials) => {
    const result: IValidationResult = await credentials.validate();

    for (let [key] of Object.entries(result.validationErrors)) {
      result.validationErrors[key] = result.validationErrors[key].map(
        (errorCode) => translate(`validators.${errorCode}`)
      );
    }

    _store.update({
      validationErrors: result.validationErrors,
      disableSubmit: Object.keys(result.validationErrors).length
    });

    return result;
  };

  const submit = async () => {
    const { credentials, languageCode } =
      _store.getState<TPasswordResetPresenter>();
    let result = await validate(credentials);
    if (result.isValid) {
      try {
        loader.start("passwordReset");
        await _application.container
          .resolve<RequestPasswordResetInteractor>("requestPasswordReset")
          .execute(credentials, languageCode);
        loader.stop("passwordReset");
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(translate("password_reset.success_message"));
      } catch (e: any) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute(e.message);
        loader.stop("passwordReset");
      }
    } else {
      _store.update({
        disableSubmit: true
      });
    }
  };

  const confirmPasswordReset = async (userId: string, token: string) => {
    try {
      loader.start("confirmPasswordReset");
      const { languageCode } = _store.getState<TPasswordResetPresenter>();
      await _application.container
        .resolve<ConfirmPasswordResetInteractor>("confirmPasswordReset")
        .execute(userId, token, languageCode);
      loader.stop("confirmPasswordReset");
      _store.update({
        confirmationMessage: translate(
          "password_reset.confirmation_success_message"
        ),
        confirmationSuccess: true
      });
    } catch (e: any) {
      _store.update({
        confirmationMessage: e.message,
        confirmationSuccess: false
      });
      loader.stop("confirmPasswordReset");
    }
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setLanguageCode,
    changeLanguage,
    setCredentialsValue,
    submit,
    confirmPasswordReset
  };
}, defaultState);

export default PasswordResetPresenter;
